import { useLazyQuery } from "@apollo/client";
import { useState, useCallback, useEffect } from "react";
import {
  LocationSearchDocument,
  LocationSearch,
} from "../../__generated__/graphql";
import { useDebounce } from "../useDebounce";

export default function useLocationSearch() {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<LocationSearch[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const [locationSearch, { loading, error }] = useLazyQuery(
    LocationSearchDocument,
    {
      onCompleted: (data) => {
        // Filter out null values and ensure type safety
        const validResults = (data?.locationSearch || []).filter(
          (item): item is LocationSearch => item !== null
        );
        setResults(validResults);
      },
      onError: (error) => {
        console.error("Location search error:", error);
        setResults([]);
      },
    }
  );

  // Execute search when debounced search term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      locationSearch({
        variables: {
          locationSearchInput: {
            search: debouncedSearchTerm,
          },
        },
      });
    } else {
      setResults([]);
    }
  }, [debouncedSearchTerm, locationSearch]);

  const search = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const clearResults = useCallback(() => {
    setSearchTerm("");
    setResults([]);
  }, []);

  return {
    search,
    searchTerm,
    results,
    loading,
    error,
    clearResults,
  };
}
